import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getPostBySlug } from '../lib/firestore';
import { sanitizeArticleHtml } from '../lib/articleHtml';
import YouTubeEmbed from '../components/YouTubeEmbed';
import { useAuth } from '../hooks/useAuth';
import Seo from '../components/Seo';
import { toAbsoluteUrl, truncateText } from '../lib/seo';

function renderRichText(value) {
  if (!value) return null;
  const urlPattern = /(https?:\/\/[^\s]+)/g;
  const lines = String(value).split('\n');

  return lines.map((line, lineIndex) => {
    const parts = line.split(urlPattern);
    return (
      <span key={`line-${lineIndex}`}>
        {parts.map((part, partIndex) => (
          /^https?:\/\//.test(part) ? (
            <a key={`part-${partIndex}`} href={part} target="_blank" rel="noreferrer">
              {part}
            </a>
          ) : (
            <span key={`part-${partIndex}`}>{part}</span>
          )
        ))}
        {lineIndex < lines.length - 1 ? <br /> : null}
      </span>
    );
  });
}

function renderPostBlock(block, index) {
  if (!block) return null;

  if (block.type === 'gallery') {
    return (
      <section key={block.id || index} className="article-block article-block-gallery">
        {block.heading ? <h2>{block.heading}</h2> : null}
        <div className="article-gallery">
          {(block.images || []).map((imageUrl) => (
            <img key={imageUrl} src={imageUrl} alt={block.caption || block.heading || '記事画像'} loading="lazy" />
          ))}
        </div>
        {block.caption ? <p className="article-caption">{renderRichText(block.caption)}</p> : null}
      </section>
    );
  }

  if (block.type === 'image') {
    return (
      <section key={block.id || index} className="article-block article-block-image">
        {block.heading ? <h2>{block.heading}</h2> : null}
        {block.imageUrl ? <img src={block.imageUrl} alt={block.heading || '記事画像'} loading="lazy" /> : null}
        {block.caption ? <p className="article-caption">{renderRichText(block.caption)}</p> : null}
      </section>
    );
  }

  if (block.type === 'imageLeft' || block.type === 'imageRight') {
    return (
      <section key={block.id || index} className={`article-block article-split ${block.type === 'imageRight' ? 'article-split-reverse' : ''}`}>
        {block.imageUrl ? <img src={block.imageUrl} alt={block.heading || '記事画像'} loading="lazy" /> : null}
        <div>
          {block.heading ? <h2>{block.heading}</h2> : null}
          {block.body ? <p>{renderRichText(block.body)}</p> : null}
          {block.caption ? <p className="article-caption">{renderRichText(block.caption)}</p> : null}
        </div>
      </section>
    );
  }

  return (
    <section key={block.id || index} className="article-block">
      {block.heading ? <h2>{block.heading}</h2> : null}
      {block.body ? <p>{renderRichText(block.body)}</p> : null}
      {block.caption ? <p className="article-caption">{renderRichText(block.caption)}</p> : null}
    </section>
  );
}

export default function PostDetailPage() {
  const { slug } = useParams();
  const { isOperator } = useAuth();
  const [post, setPost] = useState(null);

  useEffect(() => {
    getPostBySlug(slug).then(setPost);
  }, [slug]);

  if (!post) return <p>投稿を読み込み中...</p>;

  const articleBodyHtml = sanitizeArticleHtml(post.contentHtml || '');
  const description = truncateText(post.body || post.contentHtml || post.title, 140);
  const canonicalPath = `/posts/${post.slug}`;
  const coverImage = post.coverImageUrl || toAbsoluteUrl('/favicon.png');

  return (
    <>
      <Seo
        title={post.title}
        description={description}
        path={canonicalPath}
        image={coverImage}
        type="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description,
          image: [coverImage],
          datePublished: post.publishedDate || undefined,
          author: {
            '@type': 'Organization',
            name: 'Drs.Farm',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Drs.Farm',
          },
          mainEntityOfPage: toAbsoluteUrl(canonicalPath),
        }}
      />
      <article className="article">
        <Link to="/posts" className="detail-back-link">← 記事一覧へ戻る</Link>
        {isOperator ? (
          <div className="row-actions">
            <Link className="btn btn-secondary" to={`/admin/reports?editor=operator&mode=edit&postId=${post.id}`}>
              編集
            </Link>
          </div>
        ) : null}
        <h1>{post.title}</h1>
        {post.publishedDate ? <p className="meta-text">{post.publishedDate}</p> : null}
        {post.tags?.length ? <p className="meta-text">タグ: {post.tags.join(' / ')}</p> : null}
        {post.cropTags?.length ? <p className="meta-text">野菜タグ: {post.cropTags.join(' / ')}</p> : null}
        {post.youtubeUrl ? <YouTubeEmbed url={post.youtubeUrl} title={`${post.title} の動画`} /> : null}
        <p>{renderRichText(post.body)}</p>
        {articleBodyHtml ? (
          <div className="article article-rich-content" dangerouslySetInnerHTML={{ __html: articleBodyHtml }} />
        ) : (
          (post.contentBlocks || []).map(renderPostBlock)
        )}
      </article>
    </>
  );
}
